import React, { useRef, useState } from 'react';
import { Descriptions, Typography, message } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import './styles.less';
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import { useDeleteBadgeMutation } from '../../gql/hooks/useBadgeQueries';
import { ColoredTag, ViewPageActions } from '../GenericCreateEdit/GenericElements';
import { MISSING_IMAGE } from '../../consts';
import { Image } from 'antd';

dayjs.extend( localizedFormat );

const ViewBadgesPage = () => {

    const listPageRef = useRef();

    const [ deleteBadgeMutation ] = useDeleteBadgeMutation();

    const [ messageApi ] = message.useMessage();

    const [ , setExpandedRows ] = useState( [] );
    const [ deleting, setDeleting ] = useState( false );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Badges' }
    ];
    
    const handleEdit = ( record ) => {
        routeHistory.push( Routes.EditBadge.buildPath( record.id ) );
    };

    const handleDelete = async ( record ) => {
        setDeleting( true );
        messageApi.open( {
            type: 'loading',
            content: 'Deleting badge...',
            duration: 0,
        } );

        try {
            await deleteBadgeMutation( record.id );
            messageApi.destroy();
            messageApi.success( 'Badge successfully deleted!' );
        } catch ( e ) {
            messageApi.destroy();
            messageApi.error( 'Could not delete badge' );
            console.error( e );
            setDeleting( false );
            return;
        }

        setTimeout( () => {
            setDeleting( false );
            listPageRef.current?.reload();
        }, 1500 );

    };

    const renderExpandedRow = ( item ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <Descriptions bordered layout='vertical' items={ [
                            {
                                key: 'desc',
                                label: `Description (${ DEFAULT_LOCALE_CODE })`,
                                children: item.description?.length > 0 ?
                                    <Typography.Paragraph style={ { marginTop: 8 } }>
                                        { item.description }
                                    </Typography.Paragraph> :
                                    <span className='canceled'>No description</span>
                            }
                            
                        ] } />
                    </div>
                </div>
            </div>
        );
    };

    const tableColumns = [
        {
            title: 'Icon',
            dataIndex: 'image',
            key: 'image',
            render: ( thumbnailImage ) =>
                <Image 
                    width="100px"
                    className="thumbnail-image" 
                    fallback={ MISSING_IMAGE }
                    src={ thumbnailImage || MISSING_IMAGE }
                    preview={ !!thumbnailImage }
                />
        },
        {
            dataIndex: 'name',
            sortKey: 'name',
            key: 'name',
            title: 'Name',
            fixed: 'left',
        },
        {
            title: 'Users Awarded',
            dataIndex: 'personBadgeCount'
        },
        {
            key: 'isRepeatable',
            title: 'Repeatability',
            dataIndex: 'isRepeatable',
            render: val => <ColoredTag.Repeatable isRepeatable={ val } />
        },
        {
            title: 'Published',
            dataIndex: 'isPublished',
            render: val => <ColoredTag.Publish isPublished={ val } />
        },
        {
            key: 'createdAt',
            title: 'Created At',
            dataIndex: 'createdAt',
            sortKey: 'createdAt',
            render: val => val?.format( 'lll' )
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sortKey: 'updatedAt',
            render: val => val?.format( 'lll' )
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            align: 'center',
            width: '100px',
            render: ( item ) => {
                const canDelete = item.personBadgeCount === 0;
                return <ViewPageActions item={ item } handleDeleteCb={ handleDelete } deleteDisabled={ !canDelete } handleEditCb={ handleEdit } />;
            } 
        }
    ];

    function setExpandedRowsCb( rows ) {

        setExpandedRows( rows );
    }

    return <SearchQueryList
        ref={ listPageRef }
        type='badges'
        uiStrings={ {
            cardTitle: 'Badges',
            createButtonTitle: 'Create Badge'
        } }
        tableColumns={ tableColumns }
        breadcrumbs={ breadcrumbs }
        isDeleting={ deleting }
        createClickCallback={ () => routeHistory.push( Routes.CreateBadge.buildPath() ) }
        renderExpandedRowCallback={ renderExpandedRow }
        setExpandedRowsCallback={ setExpandedRowsCb }
        defaultExpandedRows={ [] }
        />;
};

export default ViewBadgesPage;
